import React, { useState, useEffect } from 'react';
import { getAircraftWithStats, createAircraft, updateAircraft } from '../utils/supabaseQueries';
import type { Aircraft, AircraftInsert, AircraftWithStats } from '../types/database';

const AircraftManagement: React.FC = () => {
  const [aircraft, setAircraft] = useState<AircraftWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingAircraft, setEditingAircraft] = useState<Aircraft | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    registration: '',
    model: 'C208',
    manufacturer: 'Cessna',
  });

  useEffect(() => {
    loadAircraft();
  }, []);

  const loadAircraft = async () => {
    try {
      setLoading(true);
      const data = await getAircraftWithStats();
      setAircraft(data);
    } catch (err) {
      console.error('Error loading aircraft:', err);
      setError('Failed to load aircraft');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAircraft = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const newAircraft = await createAircraft({
        registration: formData.registration.trim().toUpperCase(),
        model: formData.model.trim() || null,
        manufacturer: formData.manufacturer.trim() || null,
      });

      await loadAircraft(); // Reload to get with stats
      setSuccess(`Aircraft ${newAircraft.registration} added successfully!`);
      
      // Reset form
      setFormData({ registration: '', model: 'C208', manufacturer: 'Cessna' });
      setShowAddForm(false);
    } catch (err: any) {
      setError(err.message || 'Failed to add aircraft');
    }
  };

  const handleEditAircraft = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAircraft) return;

    setError(null);
    setSuccess(null);

    try {
      const updatedAircraft = await updateAircraft(editingAircraft.id, {
        registration: formData.registration.trim().toUpperCase(),
        model: formData.model.trim() || null,
        manufacturer: formData.manufacturer.trim() || null,
      });

      await loadAircraft(); // Reload to get fresh stats
      setSuccess(`Aircraft ${updatedAircraft.registration} updated successfully!`);
      setEditingAircraft(null);
      setFormData({ registration: '', model: 'C208', manufacturer: 'Cessna' });
    } catch (err: any) {
      setError(err.message || 'Failed to update aircraft');
    }
  };

  const startEdit = (ac: Aircraft) => {
    setEditingAircraft(ac);
    setFormData({
      registration: ac.registration,
      model: ac.model || 'C208',
      manufacturer: ac.manufacturer || 'Cessna',
    });
    setShowAddForm(false);
    setError(null);
    setSuccess(null);
  };

  const cancelEdit = () => {
    setEditingAircraft(null);
    setFormData({ registration: '', model: 'C208', manufacturer: 'Cessna' });
    setError(null);
  };

  const toggleAddForm = () => {
    setShowAddForm(!showAddForm);
    setEditingAircraft(null);
    setFormData({ registration: '', model: 'C208', manufacturer: 'Cessna' });
    setError(null);
    setSuccess(null);
  };

  const formatTime = (hours: number | undefined): string => {
    if (!hours) return '0.0h';
    return `${hours.toFixed(1)}h`;
  };

  const filteredAircraft = aircraft.filter(ac =>
    ac.registration.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (ac.model || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Aircraft Management</h2>
        <button
          onClick={toggleAddForm}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {showAddForm ? 'Cancel' : '+ Add New Aircraft'}
        </button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
          <p className="text-sm text-green-800">✓ {success}</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Add/Edit Form */}
      {(showAddForm || editingAircraft) && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {editingAircraft ? 'Edit Aircraft' : 'Add New Aircraft'}
          </h3>
          <form onSubmit={editingAircraft ? handleEditAircraft : handleAddAircraft} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Registration *
                </label>
                <input
                  type="text"
                  value={formData.registration}
                  onChange={(e) => setFormData(prev => ({ ...prev, registration: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., SH-ASC"
                  required
                  disabled={!!editingAircraft}
                />
                {!editingAircraft && (
                  <p className="mt-1 text-xs text-gray-500">Will be converted to uppercase</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model
                </label>
                <input
                  type="text"
                  value={formData.model}
                  onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., C208"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Manufacturer
                </label>
                <input
                  type="text"
                  value={formData.manufacturer}
                  onChange={(e) => setFormData(prev => ({ ...prev, manufacturer: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Cessna"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={editingAircraft ? cancelEdit : toggleAddForm}
                className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {editingAircraft ? 'Update Aircraft' : 'Add Aircraft'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search */}
      <div className="mb-6">
        <label htmlFor="search-aircraft" className="block text-sm font-medium text-gray-700 mb-1">
          Search Aircraft
        </label>
        <input
          type="text"
          id="search-aircraft"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by registration or model..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Aircraft List */}
      {loading ? (
        <div className="text-center py-8">
          <p className="text-gray-500">Loading aircraft...</p>
        </div>
      ) : filteredAircraft.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500">
            {searchTerm ? 'No aircraft found matching your search.' : 'No aircraft added yet. Click "+ Add New Aircraft" to get started.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Registration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Model
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Manufacturer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Hours
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Cycles
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAircraft.map((ac) => (
                <tr key={ac.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{ac.registration}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{ac.model || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{ac.manufacturer || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatTime(ac.statistics?.total_flight_hours)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {ac.statistics?.total_cycles || 0}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {ac.active ? (
                      <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                        Active
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => startEdit(ac)}
                      className="text-blue-600 hover:text-blue-800 mr-3"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Total Aircraft:</span>
            <span className="ml-2 font-medium">{aircraft.length}</span>
          </div>
          <div>
            <span className="text-gray-600">Active Aircraft:</span>
            <span className="ml-2 font-medium text-green-600">
              {aircraft.filter(a => a.active).length}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Total Flight Hours:</span>
            <span className="ml-2 font-medium">
              {formatTime(aircraft.reduce((sum, a) => sum + Number(a.statistics?.total_flight_hours || 0), 0))}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Total Cycles:</span>
            <span className="ml-2 font-medium">
              {aircraft.reduce((sum, a) => sum + (a.statistics?.total_cycles || 0), 0)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AircraftManagement;

